import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { UserMinus, Search, Calendar } from "lucide-react";

const nombre = (a) => [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ");

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function HistorialBajas() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [ciclo, setCiclo] = useState("");
  const [motivo, setMotivo] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length === 1) setPlantelId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (plantelId) params.plantel_id = plantelId;
      if (ciclo) params.ciclo = ciclo;
      if (motivo) params.motivo = motivo;
      const { data } = await api.get("/alumnos/bajas", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [plantelId, ciclo, motivo]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((a) => nombre(a).toLowerCase().includes(term) || (a.curp || "").toLowerCase().includes(term));
  }, [items, q]);

  const ciclosDisponibles = useMemo(() => {
    const s = new Set(items.map((a) => a.ciclo).filter(Boolean));
    return [...s].sort().reverse();
  }, [items]);

  const motivosDisponibles = useMemo(() => {
    const s = new Set(items.map((a) => a.motivo_baja).filter(Boolean));
    return [...s].sort();
  }, [items]);

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Auditoría</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Historial de bajas</h1>
          <p className="text-sm text-[#545454] mt-1">Consulta el listado global de alumnos con estatus <strong>BAJA</strong>. Filtra por ciclo escolar y motivo.</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 1 && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Plantel</label>
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="bajas-plantel"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos mis planteles</SelectItem>
                  {planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Ciclo escolar</label>
            <Select value={ciclo || "all"} onValueChange={(v) => setCiclo(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="bajas-ciclo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los ciclos</SelectItem>
                {ciclosDisponibles.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Motivo</label>
            <Select value={motivo || "all"} onValueChange={(v) => setMotivo(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="bajas-motivo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los motivos</SelectItem>
                {motivosDisponibles.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Buscar</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#9CA3AF]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o CURP…" className="pl-8" data-testid="bajas-search" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <div className="px-4 py-2 text-xs uppercase tracking-widest text-[#6B7280] border-b border-[#F3F4F6] flex items-center justify-between">
            <div className="inline-flex items-center gap-2"><UserMinus className="w-3.5 h-3.5 text-[#9F2241]" /> {filtered.length} baja{filtered.length === 1 ? "" : "s"}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-[10px] uppercase tracking-wider text-[#6B7280] border-b border-[#E5E7EB]">
              <tr>
                <th className="py-2.5 px-3 text-left">Alumno</th>
                <th className="py-2.5 px-3 text-left">CURP</th>
                <th className="py-2.5 px-3 text-left">Grupo · Ciclo</th>
                <th className="py-2.5 px-3 text-left">Motivo</th>
                <th className="py-2.5 px-3 text-left w-40"><Calendar className="w-3 h-3 inline mr-1" />Fecha de baja</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-10 text-center text-[#9CA3AF]">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-[#9CA3AF]">Sin bajas registradas con estos filtros</td></tr>
              ) : filtered.map((a) => (
                <tr key={a.id} className="border-b border-[#F3F4F6] hover:bg-[#FEF2F4]/40">
                  <td className="py-2 px-3">
                    <div className="text-sm font-medium uppercase text-[#7C1E36]">{nombre(a)}</div>
                    {a.plantel_nombre && <div className="text-[11px] text-[#6B7280]">{a.plantel_nombre}</div>}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs text-[#6B7280]">{a.curp || "—"}</td>
                  <td className="py-2 px-3 text-xs">
                    {a.grado != null ? `${a.grado}° "${a.letra}"` : "—"}
                    <div className="text-[10px] text-[#9CA3AF]">Ciclo {a.ciclo || "—"}</div>
                  </td>
                  <td className="py-2 px-3 text-xs">{a.motivo_baja || "—"}</td>
                  <td className="py-2 px-3 text-xs text-[#6B7280]">{fmtDate(a.fecha_baja)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  );
}
